import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Input';
import { useOutboundPushes } from '@/hooks/useDispatcher';
import { formatDateTime } from '@/lib/utils';
import type { OutboundPush } from '@/types/api';

export default function OutputsPage() {
  const [target, setTarget] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const { data, isLoading } = useOutboundPushes({
    target: target || undefined,
    status: status || undefined,
  });

  const columns = useMemo<ColumnDef<OutboundPush>[]>(
    () => [
      {
        accessorKey: 'target',
        header: 'Cible',
        cell: ({ getValue }) => <Badge tone="brand">{getValue<string>()}</Badge>,
      },
      {
        accessorKey: 'contact_id',
        header: 'Contact',
        cell: ({ getValue }) => {
          const id = getValue<string | null>();
          if (!id) return <span className="text-gray-400">—</span>;
          return (
            <Link
              to={`/contacts/${id}`}
              className="text-brand-primary hover:underline font-mono text-xs"
            >
              {id.slice(0, 8)}…
            </Link>
          );
        },
      },
      {
        accessorKey: 'target_ref',
        header: 'Ref externe',
        cell: ({ getValue }) => <span className="font-mono text-xs">{getValue<string | null>() ?? '—'}</span>,
      },
      {
        accessorKey: 'attempts',
        header: 'Essais',
        cell: ({ getValue }) => getValue<number | null>() ?? 0,
      },
      {
        accessorKey: 'status',
        header: 'Statut',
        cell: ({ getValue }) => <StatusBadge status={getValue<string | null>()} />,
      },
      {
        accessorKey: 'sent_at',
        header: 'Envoyé',
        cell: ({ getValue }) => formatDateTime(getValue<string | null>()),
      },
      {
        accessorKey: 'created_at',
        header: 'Créé',
        cell: ({ getValue }) => formatDateTime(getValue<string>()),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Outputs"
        description="Log des outbound_pushes vers Instantly / Success.ai / BIG.call / form-fill"
      />
      <DataTable
        data={data}
        columns={columns}
        loading={isLoading}
        searchPlaceholder="Filtrer par contact, ref externe, statut…"
        emptyTitle="Aucun output"
        rightSlot={
          <div className="flex items-center gap-2">
            <Select value={target} onChange={(e) => setTarget(e.target.value)} className="w-40">
              <option value="">Toutes cibles</option>
              <option value="bigcall_ingest">bigcall_ingest</option>
              <option value="instantly">instantly</option>
              <option value="successai">successai</option>
              <option value="formfill_webhook">formfill_webhook</option>
            </Select>
            <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-32">
              <option value="">Tous statuts</option>
              <option value="pending">pending</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
            </Select>
          </div>
        }
      />
    </div>
  );
}
